/**
 * Theme performance monitoring utilities
 */

class ThemePerformanceMonitor {
  constructor() {
    this.measurements = [];
    this.isMonitoring = false;
  }

  /**
   * Start monitoring theme switch performance
   */
  startMeasurement(label = 'theme-switch') {
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(`${label}-start`);
      this.isMonitoring = true;
      return label;
    }
    return null;
  }

  /**
   * End monitoring and record measurement
   */
  endMeasurement(label = 'theme-switch') {
    if (!this.isMonitoring || typeof performance === 'undefined' || !performance.mark) {
      return null;
    }

    try {
      performance.mark(`${label}-end`);
      performance.measure(label, `${label}-start`, `${label}-end`);
      
      const measurement = performance.getEntriesByName(label, 'measure')[0];
      if (measurement) {
        this.measurements.push({
          label,
          duration: measurement.duration,
          timestamp: Date.now()
        });

        // Clean up performance entries
        performance.clearMarks(`${label}-start`);
        performance.clearMarks(`${label}-end`);
        performance.clearMeasures(label);

        // Log slow theme switches in development
        if (process.env.NODE_ENV === 'development' && measurement.duration > 100) {
          console.warn(`Slow theme switch detected: ${measurement.duration.toFixed(2)}ms`);
        }

        return measurement.duration;
      }
    } catch (error) {
      console.warn('Theme performance measurement failed:', error);
    } finally {
      this.isMonitoring = false;
    }

    return null;
  }

  /**
   * Get performance statistics
   */
  getStats() {
    if (this.measurements.length === 0) {
      return null;
    }

    const durations = this.measurements.map(m => m.duration);
    const avg = durations.reduce((sum, d) => sum + d, 0) / durations.length;
    const min = Math.min(...durations);
    const max = Math.max(...durations);

    return {
      count: this.measurements.length,
      average: avg,
      min,
      max,
      measurements: this.measurements.slice(-10) // Last 10 measurements
    };
  }

  /**
   * Clear all measurements
   */
  clear() {
    this.measurements = [];
  }
}

// Singleton instance
const themePerformanceMonitor = new ThemePerformanceMonitor();

/**
 * Detect if device might have performance constraints
 */
export const detectSlowDevice = () => {
  try {
    // Check for indicators of slower devices
    const indicators = {
      // Low memory
      lowMemory: typeof navigator !== 'undefined' && 
                 navigator.deviceMemory && 
                 navigator.deviceMemory <= 2,
      
      // Slow connection
      slowConnection: typeof navigator !== 'undefined' && 
                     navigator.connection && 
                     (navigator.connection.effectiveType === 'slow-2g' || 
                      navigator.connection.effectiveType === '2g'),
      
      // Reduced motion preference (often enabled on slower devices)
      reducedMotion: typeof window !== 'undefined' && 
                    window.matchMedia && 
                    window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      
      // Old browser (rough heuristic)
      oldBrowser: typeof window === 'undefined' || 
                 !window.CSS || 
                 !window.CSS.supports || 
                 !window.CSS.supports('display', 'grid')
    };

    return Object.values(indicators).some(Boolean);
  } catch (error) {
    // If detection fails, assume it's a slower device to be safe
    console.warn('Device performance detection failed:', error);
    return true;
  }
};

/**
 * Optimize theme switching for device performance
 */
export const optimizeForDevice = () => {
  try {
    const isSlowDevice = detectSlowDevice();
    
    if (isSlowDevice && typeof document !== 'undefined') {
      // Reduce transition duration for slower devices
      const root = document.documentElement;
      if (root && root.style) {
        root.style.setProperty('--theme-transition-duration', '0.1s');
      }
      
      // Disable some animations
      if (root && root.classList) {
        root.classList.add('reduced-animations');
      }
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.warn('Device optimization failed:', error);
    return false;
  }
};

/**
 * Measure theme switch performance
 */
export const measureThemeSwitch = (callback) => {
  const label = themePerformanceMonitor.startMeasurement();
  
  return new Promise((resolve) => {
    callback();
    
    // Wait for next frame to ensure DOM updates are complete
    requestAnimationFrame(() => {
      const duration = themePerformanceMonitor.endMeasurement(label);
      resolve(duration);
    });
  });
};

/**
 * Get theme performance statistics
 */
export const getThemePerformanceStats = () => {
  return themePerformanceMonitor.getStats();
};

/**
 * Clear theme performance data
 */
export const clearThemePerformanceData = () => {
  themePerformanceMonitor.clear();
};

export default themePerformanceMonitor;
